// The presence dot (RT-01 presence in Chat, M2-06; CHAT-SWITCH-A Slack's states): over an
// avatar's corner, ringed in the surface it sits on. Active is a filled --success dot, away a
// hollow ring, do-not-disturb a small moon. It carries its own accessible name.
import { cn } from "@companyos/ui";
import { MoonIcon } from "lucide-react";

export type PresenceState = "active" | "away" | "dnd";

export const PRESENCE_LABEL: Record<PresenceState, string> = {
  active: "Active",
  away: "Away",
  dnd: "Do not disturb",
};

/** The dot's state from a status (do-not-disturb wins); no status reads as away. */
export function presenceState(
  s: { presence: "active" | "away"; dnd: boolean } | null | undefined,
): PresenceState {
  if (!s) return "away";
  return s.dnd ? "dnd" : s.presence;
}

export function PresenceDot({
  state = "active",
  label,
  className,
}: {
  state?: PresenceState;
  /** Overrides the state's name ("Active", "Away", "Do not disturb"). */
  label?: string;
  className?: string;
}) {
  const name = label ?? PRESENCE_LABEL[state];
  if (state === "dnd")
    return (
      <span
        role="img"
        aria-label={name}
        title={name}
        data-slot="presence-dot"
        data-state={state}
        className={cn(
          "flex size-2.5 items-center justify-center rounded-full bg-sidebar text-muted-foreground ring-1 ring-sidebar",
          className,
        )}
      >
        <MoonIcon aria-hidden="true" className="size-2 fill-current" />
      </span>
    );
  return (
    <span
      role="img"
      aria-label={name}
      title={name}
      data-slot="presence-dot"
      data-state={state}
      className={cn(
        "block size-2 rounded-full ring-2 ring-sidebar",
        state === "active" ? "bg-success" : "border-[1.5px] border-muted-foreground bg-sidebar",
        className,
      )}
    />
  );
}
